import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpParams } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { HelperService } from '@shared/services/helper.service';
import { Router } from '@angular/router';
import { DeviceDataChart } from '@app/shared/model/installation-centers-deatils.model';
import { KeycloakService } from '../keycloak.service';

@Injectable({
    providedIn: 'root'
})
export class TokenInterceptor implements HttpInterceptor {

    constructor(
        private helperService: HelperService,
        private router: Router,
        private keycloakService: KeycloakService
    ) {}

    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        console.log('%c[RCA - TokenInterceptor] intercept() called for URL:', 'color: #FF1493; font-weight: bold;', request.url);
        
        const isKeycloakSession = this.helperService.keycloakSession.get();
        
        console.log('%c[RCA - TokenInterceptor] Is Keycloak session:', 'color: #FF1493;', isKeycloakSession);
        console.log('%c[RCA - TokenInterceptor] Current router URL:', 'color: #FF1493;', this.router.url);
        console.log('%c[RCA - TokenInterceptor] Request URL contains /auth/iam:', 'color: #FF1493;', this.router.url.includes('/auth/iam'));
        
        if (isKeycloakSession) {
            return from(this.keycloakService.updateToken()).pipe(
                switchMap((token) => {
                    const shouldAddParams = this.checkURLForParams(request.url);
                    const customerId = this.helperService.getCustomerId();
                    const customers = this.helperService.customers.get();
                    const activeCust = customerId ? customers.find(c => Number(c.customer_id) === Number(customerId)) : null;
                    const instCenterId = this.helperService.instCenterId;
                    const deviceType = this.helperService.deviceType;

                    const headers: { [key: string]: string } = {
                        Authorization: `Bearer ${token}`,
                    };
                    // A request that names its organization explicitly (e.g. the profile fetch during an org
                    // switch) keeps it; everything else uses the stored active organization.
                    if (!request.headers.has('x-org-id')) {
                        if (customerId) {
                            headers['x-org-id'] = customerId.toString();
                        }
                        if (activeCust && activeCust.customer_uuid) {
                            headers['x-organization-id'] = activeCust.customer_uuid;
                        }
                    }

                    const authReq = request.clone({
                        setHeaders: headers,
                        ...shouldAddParams && customerId && {
                            setParams: {
                                customerId: customerId.toString(),
                                ...instCenterId && { locationId: instCenterId },
                                ...deviceType && { device_type: deviceType }
                            }
                        }
                    });
                    return next.handle(authReq);
                })
            );
        }

        const token = this.helperService.token.get();
        if (token && !this.router.url.includes('/auth/iam')) {
            console.log('%c[RCA - TokenInterceptor] Processing token - getting customer info...', 'color: #FF1493;');
            
            const customerId = this.helperService.getCustomerId() ? this.helperService.getCustomerId().toString() : null;
            const customers = this.helperService.customers.get();
            const activeCust = customerId ? customers.find(c => Number(c.customer_id) === Number(customerId)) : null;
            const instCenterId = this.helperService.instCenterId;
            const deviceType = this.helperService.deviceType;
            
            console.log('%c[RCA - TokenInterceptor] Installation Center ID:', 'color: #FF1493;', instCenterId);
            console.log('%c[RCA - TokenInterceptor] Device Type:', 'color: #FF1493;', deviceType);
            
            const shouldAddParams = this.checkURLForParams(request.url);
            console.log('%c[RCA - TokenInterceptor] Should add params for this URL:', 'color: #FF1493;', shouldAddParams);
            
            try {
                const tempHeader: { [key: string]: string } = {
                    Authorization: `Bearer ${token}`
                };
                // Keep an organization the request names explicitly (see Keycloak branch above)
                if (!request.headers.has('x-org-id')) {
                    if (customerId) {
                        tempHeader['x-org-id'] = customerId;
                    }
                    if (activeCust && activeCust.customer_uuid) {
                        tempHeader['x-organization-id'] = activeCust.customer_uuid;
                    }
                }
                if (!request.url.includes('upload-own-leaf-data') &&
                    !request.url.includes('upload-bought-leaf-data') &&
                    !request.url.includes('upload-estate-device-data')) {
                    tempHeader['Content-Type'] = 'application/json';
                }

                request = request.clone({
                    setHeaders: tempHeader,
                    ...shouldAddParams && customerId && {
                        setParams: {
                            customerId: customerId,
                            ...instCenterId && { locationId: instCenterId },
                            ...deviceType && { device_type: deviceType}
                        }
                    }
                });
                
                console.log('%c[RCA - TokenInterceptor] Request cloned successfully with headers and params', 'color: #FF1493;');
                console.log('%c[RCA - TokenInterceptor] Final request URL:', 'color: #FF1493;', request.url);
                console.log('%c[RCA - TokenInterceptor] Final request headers:', 'color: #FF1493;', request.headers);
                
            } catch (error) {
                console.log('%c[RCA - TokenInterceptor] ERROR cloning request:', 'color: #FF0000; font-weight: bold;', error);
                console.log('%c[RCA - TokenInterceptor] Proceeding with original request...', 'color: #FF1493;');
            }
        } else {
            console.log('%c[RCA - TokenInterceptor] Skipping token processing - no token or auth URL', 'color: #FF1493;');
        }
        
        console.log('%c[RCA - TokenInterceptor] Calling next.handle()...', 'color: #FF1493;');
        return next.handle(request);
    }

    private checkURLForParams(url: string) {
        console.log('%c[RCA - TokenInterceptor] checkURLForParams() called for:', 'color: #FF1493;', url);
        
        const excludedPaths = [
            '/api/customer',
            'api/onboard',
            '/api/user',
            'api/profile/food/type',
            'api/profile/type',
            'api/profile',
            'api/regions',
            'api/sites',
            'api/commercials/location',
            'api/cold-stores',
            'api/device/filter',
            'api/device/order',
            'api/device',
            'api/commodity/categories',
            '/logout',
            'api/analytics/commodity/devices',
            'download-physical/download',
            'scan/history/v2/response',
            'scan/v2/history'
        ];
        
        const matchedPath = excludedPaths.find(path => url.includes(path));
        const result = !matchedPath;
        
        console.log('%c[RCA - TokenInterceptor] Matched excluded path:', 'color: #FF1493;', matchedPath || 'None');
        console.log('%c[RCA - TokenInterceptor] Should add params result:', 'color: #FF1493;', result);
        
        return result;
    }

}
